import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { QueryColumn } from '@barthauer/abc/core/data';
import {
  AbcPopupComponent,
  AbcTextBoxComponent,
  _AbcButtonComponent,
  _AbcContextMenuComponent,
  _AbcFileUploaderComponent,
} from '@barthauer/abc/ui-components';
import { UserService } from '@barthauer/basys/core/authentication';
import { Column } from 'devextreme/ui/data_grid';
import { FlexLayoutModule } from 'ngx-flexible-layout';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { DocumentKind, GeoDsDocument } from '../../models/geods-document.model';
import { DocumentService } from '../../services/document-list.service';
import { GridComponent } from './components/grid/grid.component';
import { ListComponent } from './components/list/list.component';
import {
  Actions,
  defaultGridOptions,
  defaultLabels,
  DocumentListGridOptions,
  GeoDsDocumentViewModel,
  IActionButtonsMenuOutput,
  IActionCompleted,
  IContextMenuItems,
  IDeleteConditions,
  Labels,
  Status,
  View,
} from './document.editor.models';

const defaultContextMenuItems = () => [];

@Component({
  selector: 'abc-common-editors-documents',
  templateUrl: './documents-list.component.html',
  styleUrls: ['./documents-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FlexLayoutModule,
    MatIconModule,
    _AbcButtonComponent,
    AbcPopupComponent,
    _AbcFileUploaderComponent,
    AbcTextBoxComponent,
    _AbcContextMenuComponent,
    GridComponent,
    ListComponent,
  ],
  providers: [DocumentService],
})
export class DocumentsListComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('addDocumentPopup') addDocumentPopup: AbcPopupComponent;
  @ViewChild('fileUploader') fileUploader: _AbcFileUploaderComponent;
  @ViewChild('deletePopup') deletePopup: AbcPopupComponent;
  @ViewChild('previewVideoPopup') previewVideoPopup: AbcPopupComponent;
  @ViewChild('contextMenu') contextMenu: _AbcContextMenuComponent;
  @ViewChild('videoElement') videoElement: ElementRef<HTMLVideoElement>;
  customStyle: string;
  elementToCheck: string;
  // parentObjectTypeAndId consist of Id and ObjectType
  @Input() parentObjectTypeAndId = new Map<string, string>();
  @Input() opath: string;
  @Input() limitCount: number;
  @Input() widthPopup: string = '400px';
  @Input() allowChangeLayout = true;
  @Input() allowSearch = true;
  @Input() displayHeader = true;
  @Input() dragEnabled: boolean = true;
  @Input() additionalDocumentColumnsToPersist = new Map<string, string | number | boolean>();
  @Input() additionalDocumentColumnsToRead: QueryColumn[] = [];
  @Input() additionalGridColumns: Column[] = [];

  @Input() allowAdd = true;
  @Input() allowDownload = true;
  @Input() allowPreview = true;
  @Input() allowDelete = true;

  @Input() deactiveDeleteAfterSpecificTime = false;
  @Input() TimeInHoursToDeactivateDelete = 48;

  @Input() allowDeleteOnlyToCreator = true;
  @Input() allowMultipleUpload = false;
  @Input() allowMultipleSelection = false;

  @Input() gridOptions: DocumentListGridOptions = defaultGridOptions;

  @Input() acceptType = '*';
  @Input() maxFileSizeToUpload = 7000000; // in bytes

  @Input() height = '100%';

  @Input() userId;
  @Input() labels: Labels = defaultLabels;

  @Input() actionsContextMenu = false;
  @Output() onFileNameChanged = new EventEmitter<string>();
  @Input() DeletePopUpWidth: string = '40%';
  @Input() isGrid = true;
  @Output() isGridChange = new EventEmitter<boolean>();

  @Input() disableUploadButton = false;
  @Output() disableUploadButtonChange = new EventEmitter<boolean>();

  @Output() onFileUploaded = new EventEmitter<GeoDsDocument[]>();

  @Input() allowDetails = false;
  @Input() viewPreference: View = View.FileUploader;
  @Output() onGoToDetails = new EventEmitter<string>();

  @Output() onCloseDocumentPopup = new EventEmitter();

  @Output() dblClick = new EventEmitter<GeoDsDocumentViewModel>();

  // Action is every server request , these two outputs should be used to launch and hide apps loader
  @Output() onActionStarted = new EventEmitter();
  @Output() onActionCompleted = new EventEmitter<IActionCompleted>();
  @Input() specificContextMenuButton: (
    doc?: GeoDsDocumentViewModel,
  ) => IContextMenuItems[] | Array<(doc?: GeoDsDocumentViewModel) => IContextMenuItems[]> = defaultContextMenuItems;
  @Output() getDocumentIdUploaded = new EventEmitter<string[]>();
  private videoTag: HTMLVideoElement;

  private destroySubject: Subject<void> = new Subject();
  private updatedDocument: GeoDsDocumentViewModel | null = null;

  public _dataSource: GeoDsDocumentViewModel[] = [];

  @Input()
  set dataSource(value: GeoDsDocumentViewModel[]) {
    this._dataSource = [...value];
  }
  get dataSource(): GeoDsDocumentViewModel[] {
    return this._dataSource;
  }

  private canvas: HTMLCanvasElement;
  public canvasId = (Math.floor(Math.random() * 10000) + 1000).toString();

  public documentsToAdd = new Map<string, GeoDsDocument>();

  private documentId: string;
  public documentSize: string;
  public documentName: string;
  public documentType: string;
  public fileSizeExceedMaximumSize = false;

  private toDeleteMany = false;
  public selectedDocsIds: string[] = [];

  public isContextMenuVisible = false;
  public selectedDocument!: GeoDsDocumentViewModel;
  @Output() selectedDocumentEmitter = new EventEmitter<any>();

  public contextMenuItems: IContextMenuItems[] = [];

  public videoSrc: string;
  public videoStream: MediaStream;

  public uploadStarted = false;
  public allowCamera = false;

  public cameraImageName: string;

  constructor(
    private documentService: DocumentService,
    private userSevice: UserService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    if (!this.userId) {
      this.onActionStarted.emit();
      this.userSevice
        .getCurrentLoginInfo()
        .pipe(
          takeUntil(this.destroySubject),
          switchMap((LoginInfo) => {
            return this.documentService.prepareProfile(LoginInfo);
          }),
          switchMap((connectedUser) => {
            this.userId = connectedUser[0]?.Id;
            return this.opath ? this.getDocumentsViewModels(this.opath, true) : of([]);
          }),
        )
        .subscribe(
          (docs) => {
            this._dataSource =
              Array.isArray(docs) && docs.length > 0 ? docs.filter((data) => data.FileName !== null) : [];
            this.cdr.markForCheck();
          },
          (error) => {
            this.onActionCompleted.emit({ action: Actions.get, status: Status.fail });
          },
          () => {
            this.onActionCompleted.emit({ action: Actions.get, status: Status.success });
          },
        );
    }

    this.documentService.dataToUpload$.pipe(takeUntil(this.destroySubject)).subscribe((document: GeoDsDocument) => {
      if (!this.allowMultipleUpload) {
        this.documentName = document.FileName;
        this.documentType = document.Kind;
        this.documentSize = document.Size;
        this.documentsToAdd.clear();
      }
      this.documentsToAdd.set(document.FileName, document);
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.opath?.currentValue?.length > 0 && this.userId?.trim().length > 0) {
      this.prepareData(changes.opath.currentValue);
    }

    if (changes.labels && Object.keys(changes.labels?.currentValue).length > 0) {
      this.labels = { ...Object.assign(defaultLabels, changes.labels.currentValue) };
    }
  }
  ngOnDestroy(): void {
    this.destroySubject.next(null);
    this.destroySubject.complete();
  }

  public getDocumentsViewModels(opath: string, withObject: boolean): Observable<GeoDsDocumentViewModel[]> {
    const deleteConditions: IDeleteConditions = {
      allowDelete: this.allowDelete,
      deactiveDeleteAfterSpecificTime: this.deactiveDeleteAfterSpecificTime,
      allowDeleteOnlyToCreator: this.allowDeleteOnlyToCreator,
      TimeInHoursToDeactivateDelete: this.TimeInHoursToDeactivateDelete,
      userId: this.userId,
    };
    return this.documentService
      .getDocuments(opath, withObject, this.limitCount, this.additionalDocumentColumnsToRead)
      .pipe(
        switchMap((docs) =>
          this.documentService.convertDocumentsToDocumentsViewModels(
            docs,
            this.allowPreview,
            this.allowDownload,
            this.allowDetails,
            deleteConditions,
          ),
        ),
        takeUntil(this.destroySubject),
      );
  }

  public prepareData(opath: string): void {
    this.onActionStarted.emit();
    this.getDocumentsViewModels(opath, true).subscribe(
      (docs) => {
        this._dataSource = Array.isArray(docs) && docs.length > 0 ? docs : [];
        this.cdr.markForCheck();
      },
      (error) => {
        this.onActionCompleted.emit({ action: Actions.get, status: Status.fail });
      },
      () => {
        this.onActionCompleted.emit({ action: Actions.get, status: Status.success });
      },
    );
  }

  public updateDataSource(fn: (document: GeoDsDocumentViewModel) => GeoDsDocumentViewModel): void {
    this._dataSource = this._dataSource.map((doc) => fn(doc));
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  public updateCustomStyle(style: string, elementToCheck: string): void {
    this.customStyle = style;
    this.elementToCheck = elementToCheck;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  public uploadFile = (file: { name: string }) => {
    this.onActionStarted.emit();

    const fileToUpload: GeoDsDocument = this.documentsToAdd.get(file.name);
    this.documentsToAdd.delete(file.name);
    this.uploadStarted = true;

    const isItLastFileToAdd = this.documentsToAdd.size === 0;

    if (fileToUpload.Designation) {
      if (this.parentObjectTypeAndId.size > 0) {
        this.disableUploadButton = true;
        this.disableUploadButtonChange.emit(this.disableUploadButton);
        fileToUpload.IdRefPersonInsert = this.userId;
        const filesToPersist: GeoDsDocument[] = [];
        for (const [key, value] of this.parentObjectTypeAndId) {
          filesToPersist.push({ ...fileToUpload, ParentObjectType: value, ParentId: key });
        }

        this.documentService
          .persistUploadedFiles(filesToPersist, this.additionalDocumentColumnsToPersist)
          .pipe(
            switchMap((persistedFiles) => {
              if (Array.isArray(persistedFiles) && persistedFiles.length > 0) {
                const id = persistedFiles[0].map((file) => file.Id);
                this.getDocumentIdUploaded.emit(id);
                const opath = `Id in (${persistedFiles[0].map((file) => "'" + file.Id + "'")})`;
                return this.getDocumentsViewModels(opath, true);
              } else {
                return of([]);
              }
            }),
          )
          .pipe(takeUntil(this.destroySubject))
          .subscribe(
            (persistedFiles: GeoDsDocumentViewModel[]) => {
              if (!this._dataSource) {
                this._dataSource = [];
              }
              if (Array.isArray(persistedFiles) && persistedFiles.length > 0) {
                this._dataSource = [...persistedFiles, ...this._dataSource];
              }

              this.disableUploadButton = false;
              this.disableUploadButtonChange.emit(this.disableUploadButton);

              if (isItLastFileToAdd) {
                this.closeDocumentPopup();
              }

              this.uploadStarted = false;
              this.onActionCompleted.emit({ action: Actions.add, status: Status.success });
            },
            (error) => {
              if (isItLastFileToAdd) {
                this.closeDocumentPopup();
              }
              console.error(error);
              this.uploadStarted = false;
              this.onActionCompleted.emit({ action: Actions.add, status: Status.fail });
            },
          );
      }
    }
  };

  async detectDocumentChanges(field: string, e: any, file?: File): Promise<void> {
    if (!this.selectedDocument) return;

    this.selectedDocument = {
      ...this.selectedDocument,
      [field]: e.value,
    };

    if (field === 'Object') {
      this.selectedDocument = await this.convertObject(this.selectedDocument);
      this.selectedDocument.FileName = file?.name;
      this.selectedDocument.Comment = '';
    }
  }

  public onFileUpload(event: { value: any[] }): void {
    const files = event.value;
    if (files && files.length > 0) {
      this.onFileNameChanged.emit(files[0].name);
    }
    if (!this.allowMultipleUpload && event.value.length > 0) {
      const sizeOfFileToUpload = event.value[0].size;
      this.fileSizeExceedMaximumSize = sizeOfFileToUpload > this.maxFileSizeToUpload;
    }

    this.onFileUploaded.emit(this._dataSource);
    this.documentService.onFileChanged(event?.value ?? []);

    this.detectDocumentChanges('Object', event, files[0]);
  }
  public showAddDocumentPopup(): void {
    this.addDocumentPopup.instance.show();
    if (this.viewPreference === View.Camera) {
      this.startVideo();
    }
  }

  public goToDetails(docId: string): void {
    this.onGoToDetails.emit(docId);
  }

  public async closeDocumentPopup(): Promise<void> {
    this.documentsToAdd.clear();
    this.deleteImage();
    this.fileUploader?.instance.reset();
    this.fileSizeExceedMaximumSize = false;
    this.clearFileInformations();
    await this.stopCamera();

    this.allowCamera = false;
    this.canvas = null;
    this.addDocumentPopup.instance.hide();
    this.onCloseDocumentPopup.emit();
  }

  public openActionButtonsMenu(event: IActionButtonsMenuOutput): void {
    this.selectedDocument = event.selectedDocument;
    this.selectedDocumentEmitter.emit(this.selectedDocument);
    this.contextMenuItems = [
      {
        text: this.labels.Preview,
        icon: 'fullscreen',
        visible: this.selectedDocument.allowPreview,
        onClick: (event) => {
          this.previewDocument(this.selectedDocument?.Id);
        },
      },
      {
        text: this.labels.Download,
        icon: 'spindown',
        visible: this.selectedDocument.allowDownload,
        onClick: (event) => {
          this.downloadDocument(this.selectedDocument?.Id);
        },
      },
      {
        text: this.labels.Delete,
        icon: 'trash',
        visible: this.selectedDocument.allowDelete,
        onClick: (event) => {
          this.openDeleteDocumentPopup(this.selectedDocument?.Id);
        },
      },
      {
        text: this.labels.Details,
        icon: 'chevronright',
        visible: this.selectedDocument.allowDetails,
        onClick: (event) => {
          this.goToDetails(this.selectedDocument?.Id);
        },
      },
      ...(Array.isArray(this.specificContextMenuButton)
        ? this.specificContextMenuButton
            .map((fn) => fn(this.selectedDocument))
            .reduce((acc, items) => acc.concat(items), [])
        : this.specificContextMenuButton
          ? this.specificContextMenuButton(this.selectedDocument)
          : []),
    ];
    this.cdr.markForCheck();

    const targetElement = event.target;
    this.contextMenu.position = {
      my: 'right top',
      at: 'right bottom',
      of: targetElement,
    };

    this.isContextMenuVisible = !this.isContextMenuVisible;
    this.contextMenu.instance.toggle(this.isContextMenuVisible);
  }

  public onContextMenuHidden(): void {
    this.isContextMenuVisible = false;
  }

  public downloadDocument(id: string): void {
    if (id?.trim()) {
      this.onActionStarted.emit();
      this.getDocumentById(id).subscribe(
        (doc) => {
          this.documentService.downloadFile(doc[0]);
          this.onActionCompleted.emit({ action: Actions.download, status: Status.success });
        },
        (error) => {
          console.error(error);
          this.onActionCompleted.emit({ action: Actions.download, status: Status.fail });
        },
      );
    }
  }

  public downloadMultipleDocument(): void {
    this.onActionStarted.emit();
    this.getMultipleDocumentByIds(this.selectedDocsIds)
      .pipe(
        map((docs) => {
          return docs.filter((doc) => doc.allowDownload);
        }),
      )
      .subscribe(
        (docs) => {
          docs.forEach((doc) => {
            this.documentService.downloadFile(doc);
          });
          this.onActionCompleted.emit({ action: Actions.download, status: Status.success });
        },
        (error) => {
          console.error(error);
          this.onActionCompleted.emit({ action: Actions.download, status: Status.fail });
        },
      );
  }

  public previewDocument(id: string): void {
    if (id?.trim()) {
      this.onActionStarted.emit();
      this.getDocumentById(id).subscribe(
        (doc) => {
          if (doc[0].FileName.split('.')[1] === 'mp4') {
            this.videoSrc = `data:video/mp4;base64,${doc[0].Object}`;

            this.previewVideoPopup.instance.show();
          } else {
            this.documentService.previewFile(doc[0]);
          }
          this.onActionCompleted.emit({ action: Actions.preview, status: Status.success });
        },
        (error) => {
          console.error(error);
          this.onActionCompleted.emit({ action: Actions.preview, status: Status.fail });
        },
      );
    }
  }

  private getDocumentById(documentId: string): Observable<GeoDsDocumentViewModel[]> {
    const opath = 'Id=' + "'" + documentId + "'";
    return this.getDocumentsViewModels(opath, true);
  }

  private getMultipleDocumentByIds(ids: string[]): Observable<GeoDsDocumentViewModel[]> {
    const opath = 'Id in ' + `(${ids.map((docId) => "'" + docId + "'")})`;
    return this.getDocumentsViewModels(opath, true);
  }

  public openDeleteDocumentPopup(id: string, toDeleteMany: boolean = false): void {
    if (!toDeleteMany) {
      this.documentId = id;
    }
    this.toDeleteMany = toDeleteMany;
    this.deletePopup.instance.show();
  }

  public closeDeletePopup() {
    this.deletePopup.instance.hide();
  }

  public persistDelete() {
    this.onActionStarted.emit();
    if (this.toDeleteMany) {
      if (this.selectedDocsIds.length > 0) {
        const validIdsToDelete = this._dataSource
          .filter((doc) => this.selectedDocsIds.includes(doc.Id) && doc.allowDelete)
          .map((doc) => doc.Id);
        this.documentService.deleteMultipleDocumentService(validIdsToDelete).subscribe(
          (deleted) => {
            const deletedDocumentIds: string[] = deleted[0].map((doc) => doc.Id);
            this._dataSource = [...this._dataSource.filter((item) => !deletedDocumentIds.includes(item.Id))];

            this.deletePopup.instance.hide();
            this.selectedDocsIds = [];
            this.onActionCompleted.emit({ action: Actions.delete, status: Status.success });
            this.cdr.markForCheck();
          },
          (error) => {
            this.onActionCompleted.emit({ action: Actions.delete, status: Status.fail });
          },
        );
      } else {
        this.deletePopup.instance.hide();
      }
    } else {
      this.documentService.deleteDocumentService(this.documentId).subscribe(
        () => {
          this._dataSource = [...this._dataSource.filter((item) => item.Id !== this.documentId)];
          this.deletePopup.instance.hide();
          this.onActionCompleted.emit({ action: Actions.delete, status: Status.success });
          this.cdr.markForCheck();
        },
        (error) => {
          console.error(error);
          this.onActionCompleted.emit({ action: Actions.delete, status: Status.fail });
        },
      );
    }
  }

  public displayView(value: boolean): void {
    this.isGrid = value;
    this.isGridChange.emit(this.isGrid);
  }

  public onAddPopupShown(e): void {
    e.component.content().parentElement.parentElement.classList.add('dl-add-popup');
  }

  public onDeletePopupShown(e): void {
    e.component.content().parentElement.parentElement.classList.add('dl-delete-popup');
  }

  public rowDbClick(event: GeoDsDocumentViewModel): void {
    this.dblClick.emit(event);
  }

  // --------------------------- Camera
  public clickSwitchButton(): void {
    this.allowCamera = !this.allowCamera;
    this.clearFileInformations();
    if (!this.allowCamera) {
      this.stopCamera();
    } else {
      setTimeout(() => {
        this.startVideo();
      }, 500);
    }
  }

  private async startVideo(): Promise<void> {
    this.videoTag = this.videoElement.nativeElement;
    this.videoStream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: true,
    });
    this.videoTag.srcObject = this.videoStream;
    this.videoTag.play();
    this.cdr.markForCheck();
  }

  private async stopCamera(): Promise<void> {
    if (this.videoElement && this.videoTag) {
      this.videoTag.srcObject = null;
      if (this.videoStream && this.videoTag) {
        this.videoStream.getTracks().forEach((track) => {
          track.stop();
        });
        this.videoStream = null;
        this.videoTag = null;
        this.cameraImageName = '';
      }
    }
  }

  private convertImageToFileFormat(): GeoDsDocument {
    const randomNumber = Math.floor(Math.random() * 9000) + 1000;
    const doc = new GeoDsDocument(null);
    const dataUrl = this.canvas.toDataURL('image/jpg');
    doc.Object = dataUrl;
    this.cameraImageName = 'image' + randomNumber + '.jpg';
    doc.FileName = this.cameraImageName;
    const timestamp = new Date().getTime();
    const sizeInBytes = dataUrl.length;
    doc.Designation = timestamp.toString();
    doc.Size = sizeInBytes / 1000 + ' KB';
    doc.Kind = DocumentKind.Bild;
    return doc;
  }

  public capture(): void {
    this.canvas = document.getElementById(this.canvasId) as HTMLCanvasElement;
    this.canvas.style.display = 'block';
    this.canvas.width = this.videoTag.videoWidth;
    this.canvas.height = this.videoTag.videoHeight;
    this.videoTag.style.display = 'none';

    const context = this.canvas.getContext('2d');
    context.drawImage(this.videoTag, 0, 0);
    const doc = this.convertImageToFileFormat();
    this.fileSizeExceedMaximumSize = parseInt(doc.Size) > this.maxFileSizeToUpload;

    this.documentService.updateDocumentsList(doc);
  }

  public deleteImage(): void {
    const videoSource = this.videoElement?.nativeElement;
    if (videoSource) {
      videoSource.style.display = 'block';
    }
    if (this.canvas) {
      this.canvas.style.display = 'none';
    }
    this.cameraImageName = '';
    this.clearFileInformations();
  }

  private clearFileInformations(): void {
    this.documentSize = '';
    this.documentName = '';
    this.documentType = '';
  }

  public persistEditDocument(): void {
    const docToSave = this.updatedDocument ?? this.selectedDocument;
    if (!docToSave) return;
    this.documentService
      .updateDocument(docToSave.Id, docToSave)
      .pipe(switchMap(() => this.getDocumentsViewModels(this.opath, true)))
      .subscribe({
        next: (docs: GeoDsDocumentViewModel[]) => this.handleSuccess(docs),
        error: (err) => this.handleError(err),
      });
  }
  private handleSuccess(docs: GeoDsDocumentViewModel[]): void {
    this.dataSource = docs;
    this.addDocumentPopup.instance.hide();

    this.onActionCompleted.emit({ action: Actions.edit, status: Status.success });
  }
  private handleError(err: any): void {
    console.error(err);

    this.onActionCompleted.emit({
      action: Actions.edit,
      status: Status.fail,
    });
  }
  public fileToBase64Only(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];

        resolve(base64);
      };

      reader.onerror = (error) => reject(error);
    });
  }
  public async convertObject(obj: any): Promise<any> {
    if (!obj?.Object || obj.Object.length === 0) return obj;

    const file: File = obj.Object[0];

    const base64 = await this.fileToBase64Only(file);

    return {
      ...obj,
      Object: base64,
    };
  }
}
